<template>
  <div data-cy="products-mobile-view">
    <section class="search-section" data-cy="search-section">
      <div class="search-wrapper">
        <TableSearch
          class="product-mobile-search"
          :value="searchInput"
          placeholder="Search by Name or Company or Brand or UKD ID"
          @searchSubmitted="onSearchSubmitted"
          data-cy="products-search"
        />
      </div>

      <span
        class="filter-icon"
        @click="showFilters = !showFilters"
        data-cy="filters-button"
      >
        <FilterIcon />
      </span>
    </section>
    <ProductFiltersMenu
      v-if="showFilters"
      :getPropValues="getPropValuesAsync"
      :filters="productFilters"
      @apply="onFiltersApplied"
      @clear="onFiltersCleared"
      @close="showFilters = false"
    />
    <div class="sort-wrapper">
      <DropDownList
        class="sort-dropdown"
        :textField="'text'"
        :data-items="sortOptions"
        v-model="selectedSortOption"
        @change="onSortChanged"
        data-cy="sort-dropdown"
      />
    </div>
    <ProductList
      :totalProducts="totalProducts"
      :products="products"
      :defaultTake="productsTake"
      :skip="productsSkip"
      :loading="productsLoading"
      @productsChanged="onProductsChanged"
    />
  </div>
</template>

<script setup lang="ts">
import FilterIcon from "@/icons/FilterIcon.vue";
import { useMainStore } from "@/stores/useMainStore";
import { onBeforeMount, ref, watchEffect } from "vue";
import { api } from "@/api";
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import ProductList from "./ProductList.vue";
import ProductFiltersMenu from "./ProductFiltersMenu.vue";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import type { GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest } from "@/api/ukdProducts";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { FilterField } from "@/Domain/FilterField";
import type { FilterGroup } from "@/Domain/FilterGroup";
import { FilterValuesTake } from "@/shared/helpers/filtersHelper";
import { setQueryParameter } from "@/shared/helpers/urlHelper";

const main = useMainStore();
const searchInput = ref<string>(main.UkdProductFilterCriteria);
const totalProducts = ref<number>(0);
const products = ref<OuKashUnionUkdProducts[]>([]);
const productsLoading = ref<boolean>(false);
const productsTake = ref<number>(25);
const productsSkip = ref<number>(0);
const showFilters = ref<boolean>(false);
const productFilters = ref<FilterMenuItem[]>([]);

const sortOptions = [
  {
    text: "Sort By",
    value: "",
  },
  {
    text: "Label Name & Num",
    value: "",
  },
  {
    text: "Brand",
    value: "BrandName",
  },
];

const selectedSortOption = ref<{ text: string; value: string }>(sortOptions[0]);

const getMatchingProductsAsync = async function (
  searchString: string,
  skip: number,
  take: number,
  valuesForFieldOnly: string = ""
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  if (!valuesForFieldOnly) {
    productFilters.value.forEach((f) => {
      const filterGroup = {
        FilterFields: [],
        BooleanLogicOperator: EnumFilterStatementConnector.And,
        FieldName: f.field,
        InternalFieldName: "",
      } as FilterGroup;
      const filterField = {
        Operator: f.getOpValue ? f.getOpValue(f.value) : "eq",
        Value: f.getFilterValue ? f.getFilterValue(f.value) : f.value,
      } as FilterField;
      filterGroup.FilterFields.push(filterField);

      filterModel.FilterGroups.push(filterGroup);
    });
  }

  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchString,
    caller: main.userLogin,
    filters: JSON.stringify(filterModel),
    skip: skip,
    take: take,
    includeConfidentials: false,
    sortField: selectedSortOption.value.value,
    ascending: true,
    valuesForFieldOnly: valuesForFieldOnly,
  } as GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest;
  const endpoint = "GetMatchingProductsOnUkdOnSingleSearchFieldWithFiltering";

  if (!valuesForFieldOnly) return api.get(endpoint, requestParams);

  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getResponseAsync(
    createCacheKey(requestParams, endpoint),
    () => api.get(endpoint, requestParams)
  );
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onProductsChanged = function (skip: number, take: number) {
  productsTake.value = take;
  productsSkip.value = skip;
  searchMatchingProductsAsync(skip, true);
};

const onFiltersApplied = function (filters: FilterMenuItem[]) {
  productFilters.value = filters;
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onSortChanged = function () {
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const onFiltersCleared = function () {
  productFilters.value = [];
  productsSkip.value = 0;
  searchMatchingProductsAsync();
};

const searchMatchingProductsAsync = async function (
  skip: number = 0,
  appendProducts: boolean = false
) {
  if (productsLoading.value) return;
  productsLoading.value = true;
  await handleResponsePromiseAsync(
    getMatchingProductsAsync(searchInput.value, skip, productsTake.value),
    (data) => {
      if (!appendProducts) products.value = data.ListItems;
      else products.value = [...products.value, ...data.ListItems];
      totalProducts.value = data.TotalItemsInList;
    },
    "UkdProductsMobileView.vue",
    "SearchMatchingProducts"
  );
  productsLoading.value = false;
};

const getPropValuesAsync = async function (field: string) {
  let items: OuKashUnionUkdProducts[] = [];
  await handleResponsePromiseAsync(
    getMatchingProductsAsync(searchInput.value, 0, 999999, field),
    (data) => {
      console.debug(
        `UkdProductsMobileView.vue: getPropValuesAsync(), result:`,
        data
      );
      items = data.ListItems;
    },
    "UkdProductsMobileView.vue",
    "getPropValuesAsync"
  );
  return items;
};

watchEffect(() => {
  console.debug(
    "UkdProductsMobileView.vue, searchInput watch updated:",
    searchInput.value
  );
  main.UkdProductFilterCriteria = searchInput.value;
  setQueryParameter("search", searchInput.value);
});

onBeforeMount(async () => {
  await searchMatchingProductsAsync();
});
</script>

<style scoped lang="scss">
.search-section {
  background: #222;
  border-top: 1px solid #333;
  display: flex;
  align-items: center;
  padding: 10px;

  .search-wrapper {
    position: relative;
    display: flex;
    flex: 1;

    .product-mobile-search {
      width: 100%;
    }
  }

  .filter-icon {
    margin-left: 6px;
    cursor: pointer;
    padding: 5px 15px;
  }
}

.sort-wrapper {
  display: flex;
  align-items: center;
  margin-left: 10px;
  margin-right: 10px;
  margin-top: 24px;
  justify-content: center;

  .sort-dropdown {
    width: 100%;
    max-width: 340px;
  }
}
</style>
